import { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js';
import { MetricsDashboard, type MetricsDashboardMetric } from '../components/arc/metrics-dashboard/metrics-dashboard';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import { ArcDialog, ArcHeaderPeriodSelect, ArcPage, ArcPageHeader, ArcPanel, ArcSelect, ArcSummaryGrid, ArcTabs, ArcToolbar } from '../components/arc/primitives/arc-primitives';
import { useFeather } from '../shell/useFeather';
import { useTabParam } from '../shell/useTabParam';
import { getAffilmasterDashboard, getAffilmasterFinanceTimeseries, getAffilmasterFinanceMonthDetail, getAffilmasterVipComparison, getAffilmasterVipMonthBreakdown, AffilmasterApiError, type AffilmasterDashboard, type AffFinanceTimeseries, type AffFinancePeriodMetrics, type AffFinanceMonthDetail, type VipMode, type VipComparison, type VipCompareSplit, type VipCompareMetrics, type VipMonthBreakdown } from '../api/affilmaster';
import { VipBadge } from './AffilmasterAffiliatesPage';
import './affilmaster-dashboard.css';
import './affilmaster-detail.css';
import './affilmaster-affiliates-overview.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, PointElement, LineElement, Filler, Tooltip, Legend);

const fmt = (v: number) => Number(v || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 });
const fmtCZK = (v: number) => { const n = Number(v) || 0; return n ? `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} Kč` : '-'; };
const fmtEUR = (v: number) => { const n = Number(v) || 0; return n ? `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} €` : '-'; };
const MONTH_CS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
const MONTH_SHORT = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
const ymLabel = (ym: string) => { if (!/^\d{4}-\d{2}$/.test(ym)) return ym; const [y, m] = ym.split('-'); return `${MONTH_CS[Number(m) - 1] ?? m} ${y ?? ''}`; };
const ymShort = (ym: string) => { const [y, m] = ym.split('-'); return `${MONTH_SHORT[Number(m) - 1] ?? m} ${(y ?? '').slice(2)}`; };
const pctOf = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)} %` : '0 %');

function DeltaBadge({ curr, prev }: { curr: number; prev: number | null | undefined }) {
  if (prev == null || prev === 0) return <span className="am-affiliates-delta is-neutral">-</span>;
  const pct = ((curr - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <span className={`am-affiliates-delta ${up ? 'is-up' : 'is-down'}`}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{pct.toFixed(0)} %
    </span>
  );
}
const CZ_GREEN = '#137333';
const SK_BLUE = '#174ea6';

type AffPrehledTab = 'zakladni' | 'affiliates' | 'segmenty' | 'vypis' | 'vip';
const AFF_PREHLED_TABS: readonly AffPrehledTab[] = ['zakladni', 'affiliates', 'segmenty', 'vypis', 'vip'];
type VipParam = 'vse' | 'bez' | 'jen';
const VIP_PARAMS: readonly VipParam[] = ['vse', 'bez', 'jen'];
const VIP_MODE: Record<VipParam, VipMode> = { vse: 'all', bez: 'without', jen: 'only' };
const VIP_LABEL: Record<VipParam, string> = { vse: 'Celkem', bez: 'Bez VIP', jen: 'Jen VIP' };

export function AffilmasterAffiliatesPrehledyPage() {
  const [ym, setYm] = useState('');
  const [latestYm, setLatestYm] = useState('');
  const [data, setData] = useState<AffilmasterDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useTabParam<AffPrehledTab>('tab', AFF_PREHLED_TABS, 'zakladni');
  const [vipParam, setVipParam] = useTabParam<VipParam>('vip', VIP_PARAMS, 'vse', false);
  const vipMode = VIP_MODE[vipParam];
  const [perfMetric, setPerfMetric] = useState<'deposits' | 'ngr' | 'commission' | 'registrations'>('deposits');

  useEffect(() => {
    let cancelled = false;
    getAffilmasterDashboard(ym || undefined, vipMode)
      .then((d) => { if (!cancelled) { setData(d); setLatestYm((current) => current || d.ym); setErr(null); } })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení přehledů selhalo'); });
    return () => { cancelled = true; };
  }, [ym, vipMode]);

  useFeather([data, tab, perfMetric]);

  const monthOpts = useMemo(() => {
    const end = latestYm;
    if (!end) return [];
    const [y, m] = end.split('-').map(Number);
    const out: string[] = [];
    const d = new Date(y!, m! - 1, 1);
    for (let i = 0; i < 24; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); d.setMonth(d.getMonth() - 1); }
    return out;
  }, [latestYm]);

  return (
    <ArcPage className="affms-dash am-affiliates-overview">
      <ArcPageHeader
        eyebrow="AFFILMASTER"
        title="Affiliates"
        description="Přehled výkonu affiliate partnerů"
        meta={data ? (
          <div className="am-affiliates-head-meta" aria-label="Kontext přehledu">
            <ArcHeaderPeriodSelect value={data.ym} onChange={setYm} options={monthOpts.map((option) => ({ value: option, label: ymLabel(option) }))} />
            <span>1 € = {data.rate.toLocaleString('cs-CZ', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} Kč</span>
          </div>
        ) : null}
      />
      {err && (
        <div className="prototype-notice prototype-notice-error" role="alert">
          <i data-feather="alert-circle" />
          <div><strong>Přehled affiliates se nepodařilo načíst</strong><span>{err}</span></div>
        </div>
      )}
      {!err && !data && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}

      {data && (
        <>
          <div className="prototype-overview-toolbar">
            <ArcTabs
              label="Přehled affiliates"
              value={tab}
              onChange={setTab}
              items={[
                { value: 'zakladni', label: 'Affiliates' },
                { value: 'affiliates', label: 'Výkon' },
                { value: 'segmenty', label: 'Segmenty' },
                { value: 'vypis', label: 'Výpis' },
                { value: 'vip', label: 'VIP srovnání' },
              ]}
            />
            <div className="am-affiliates-toolbar-right">
              {(tab === 'zakladni' || tab === 'affiliates' || tab === 'vypis') && (
                <div className="am-vip-switch" role="radiogroup" aria-label="Započtení VIP partnerů">
                  {VIP_PARAMS.map((v) => (
                    <button key={v} type="button" role="radio" aria-checked={vipParam === v} className={vipParam === v ? 'active' : ''} onClick={() => setVipParam(v)}>{VIP_LABEL[v]}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {vipParam !== 'vse' && tab !== 'vip' && tab !== 'segmenty' && (
            <div className="am-vip-note">Zobrazeno <strong>{VIP_LABEL[vipParam].toLowerCase()}</strong>: {vipParam === 'bez' ? 'bez výsledků VIP partnerů z vlastních instancí kasin.' : 'jen výsledky VIP partnerů z vlastních instancí kasin.'}</div>
          )}

          {tab === 'zakladni' && <TabZakladni d={data} />}
          {tab === 'affiliates' && <TabVykon d={data} metric={perfMetric} setMetric={setPerfMetric} />}
          {tab === 'segmenty' && <TabSegmenty />}
          {tab === 'vypis' && <TabVypis vipMode={vipMode} />}
          {tab === 'vip' && <TabVipSrovnani ym={data.ym} />}
        </>
      )}
    </ArcPage>
  );
}

function MarketHead({ mk, name, meta }: { mk: 'cz' | 'sk'; name: string; meta: string }) {
  return (
    <div className="am-affiliates-market-head">
      <span className={`market-flag market-flag-${mk}`}>{mk.toUpperCase()}</span>
      <div><strong>{name}</strong><span>{meta}</span></div>
    </div>
  );
}

function TabZakladni({ d }: { d: AffilmasterDashboard }) {
  const total = d.activity.total, active = d.activity.active, inactive = d.activity.inactive;
  const newM = d.cz.month.new_affiliates + d.sk.month.new_affiliates;

  const trendChart = {
    labels: d.trend.labels.map(ymShort),
    datasets: [
      { label: 'CZ', data: d.trend.cz, backgroundColor: CZ_GREEN, borderRadius: 3 },
      { label: 'SK', data: d.trend.sk, backgroundColor: SK_BLUE, borderRadius: 3 },
    ],
  };
  const trendOpts = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } } };
  const doughnut = { labels: ['Aktivní', 'Neaktivní'], datasets: [{ data: [active, inactive], backgroundColor: [CZ_GREEN, '#d0d4da'], borderWidth: 0 }] };
  const doughnutOpts = { responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { position: 'bottom' as const } } };
  const activePctTxt = pctOf(active, total);
  const centerText = {
    id: 'centerText',
    afterDraw(chart: { ctx: CanvasRenderingContext2D; chartArea: { left: number; right: number; top: number; bottom: number } }) {
      const { ctx, chartArea } = chart;
      const cx = (chartArea.left + chartArea.right) / 2;
      const cy = (chartArea.top + chartArea.bottom) / 2 - 8;
      ctx.save();
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = CZ_GREEN; ctx.font = '700 1.6rem system-ui, sans-serif';
      ctx.fillText(activePctTxt, cx, cy);
      ctx.fillStyle = '#9aa0a6'; ctx.font = '600 0.62rem system-ui, sans-serif';
      ctx.fillText('AKTIVNÍ', cx, cy + 22);
      ctx.restore();
    },
  };

  const mkCard = (mk: 'cz' | 'sk', name: string, b: AffilmasterDashboard['cz']) => (
    <section className={`am-affiliates-market-block am-affiliates-market-${mk}`} key={mk}>
      <MarketHead mk={mk} name={name} meta={`Aktivita v ${mk.toUpperCase()} kampaních`} />
      <div className="am-affiliates-market-cards">
        <MetricCard label="Celkem" value={b.total.affiliates_total} context="partnerů v síti" />
        <MetricCard label="Aktivní" value={b.month.affiliates_active} context={`${pctOf(b.month.affiliates_active, b.total.affiliates_total)} ze sítě`} />
        <MetricCard label="Neaktivní" value={Math.max(0, b.total.affiliates_total - b.month.affiliates_active)} context="ve zvoleném období" />
        <MetricCard label="Noví" value={b.month.new_affiliates} context={ymLabel(d.ym)} />
      </div>
    </section>
  );

  return (
    <div className="tab-content am-affiliates-dashboard">
      <ArcSummaryGrid className="am-affiliates-component-summary">
        <MetricCard label="Affiliate síť" value={total} context="CZ a SK celkem" />
        <MetricCard label="Aktivní v měsíci" value={active} context={`${pctOf(active, total)} ze sítě`} />
        <MetricCard label="Neaktivní" value={inactive} context={`${pctOf(inactive, total)} ze sítě`} />
        <MetricCard label="Noví v měsíci" value={newM} context="první aktivita" />
      </ArcSummaryGrid>

      <section className="am-affiliates-section" aria-labelledby="am-affiliates-markets-title">
        <div className="am-affiliates-section-head">
          <div><span>TRHY</span><h2 id="am-affiliates-markets-title">Rozdělení affiliate sítě</h2></div>
          <p>Aktivita ve zvoleném období</p>
        </div>
        <div className="am-affiliates-markets">
          {mkCard('cz', 'Český trh', d.cz)}
          {mkCard('sk', 'Slovenský trh', d.sk)}
        </div>
      </section>

      <section className="am-affiliates-section" aria-labelledby="am-affiliates-charts-title">
        <div className="am-affiliates-section-head">
          <div><span>VÝVOJ</span><h2 id="am-affiliates-charts-title">Aktivita affiliate sítě</h2></div>
          <p>Posledních 12 měsíců</p>
        </div>
        <div className="am-affiliates-charts">
          <ArcPanel as="article" className="am-affiliates-chart-card am-affiliates-chart-trend">
            <header>
              <h3>Noví affiliates po měsících</h3>
              <div className="am-affiliates-chart-legend" aria-label="Legenda">
                <span><i className="is-cz" />CZ</span>
                <span><i className="is-sk" />SK</span>
              </div>
            </header>
            <div className="am-affiliates-chart-canvas"><Bar data={trendChart} options={trendOpts as never} /></div>
          </ArcPanel>
          <ArcPanel as="article" className="am-affiliates-chart-card am-affiliates-chart-mix">
            <header><h3>Aktivní vs. neaktivní</h3></header>
            <div className="am-affiliates-chart-canvas"><Doughnut data={doughnut} options={doughnutOpts as never} plugins={[centerText as never]} /></div>
          </ArcPanel>
        </div>
      </section>
    </div>
  );
}

function TabVykon({ d, metric, setMetric }: { d: AffilmasterDashboard; metric: 'deposits' | 'ngr' | 'commission' | 'registrations'; setMetric: (m: 'deposits' | 'ngr' | 'commission' | 'registrations') => void }) {
  const metricKeyMap = { deposits: 'deposits_volume', ngr: 'ngr_volume', commission: 'commission', registrations: 'registrations' } as const;
  const fkey = metricKeyMap[metric];
  const isMoney = metric !== 'registrations';
  const perfChart = {
    labels: d.financeTrend.labels.map(ymShort),
    datasets: [
      { label: 'CZ (Kč)', data: d.financeTrend.cz.map((m) => m[fkey]), borderColor: CZ_GREEN, backgroundColor: `${CZ_GREEN}22`, tension: 0.3, borderWidth: 2, pointRadius: 2, yAxisID: 'y' },
      { label: 'SK (€)', data: d.financeTrend.sk.map((m) => m[fkey]), borderColor: SK_BLUE, backgroundColor: `${SK_BLUE}22`, tension: 0.3, borderWidth: 2, pointRadius: 2, yAxisID: isMoney ? 'y1' : 'y' },
    ],
  };
  const perfOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const } },
    scales: isMoney
      ? { x: { grid: { display: false } }, y: { type: 'linear' as const, position: 'left' as const, beginAtZero: true }, y1: { type: 'linear' as const, position: 'right' as const, beginAtZero: true, grid: { drawOnChartArea: false } } }
      : { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
  };

  const registrationsMonth = d.cz.month.registrations + d.sk.month.registrations;
  const perfCard = (mk: 'cz' | 'sk', name: string, b: AffilmasterDashboard['cz'], currency: string) => (
    <section className={`am-affiliates-market-block am-affiliates-market-${mk}`} key={mk}>
      <MarketHead mk={mk} name={name} meta="Zvolené období · celkový součet pod hodnotou" />
      <div className="am-affiliates-market-cards am-affiliates-performance-cards">
        <MetricCard label="Registrace" value={b.month.registrations} context={`celkem ${fmt(b.total.registrations)}`} />
        <MetricCard label="Vklady" value={b.month.deposits_volume} suffix={currency} context={`celkem ${fmt(b.total.deposits_volume)} ${currency.trim()}`} />
        <MetricCard label="NGR" value={b.month.ngr_volume} suffix={currency} context={`celkem ${fmt(b.total.ngr_volume)} ${currency.trim()}`} />
        <MetricCard label="Provize" value={b.month.commission} suffix={currency} context={`celkem ${fmt(b.total.commission)} ${currency.trim()}`} />
        <MetricCard label="FTD" value={b.month.ftd_count} context={`celkem ${fmt(b.total.ftd_count)}`} />
        <MetricCard label="Výběry" value={b.month.withdrawals_count} context={`celkem ${fmt(b.total.withdrawals_count)}`} />
      </div>
    </section>
  );

  return (
    <div className="tab-content am-affiliates-dashboard">
      <ArcSummaryGrid className="am-affiliates-component-summary">
        <MetricCard label="Registrace" value={registrationsMonth} context="CZ a SK za měsíc" />
        <MetricCard label="Vklady CZ" value={d.cz.month.deposits_volume} suffix=" Kč" context={`SK ${fmtEUR(d.sk.month.deposits_volume)}`} />
        <MetricCard label="NGR CZ" value={d.cz.month.ngr_volume} suffix=" Kč" context={`SK ${fmtEUR(d.sk.month.ngr_volume)}`} />
        <MetricCard label="Provize CZ" value={d.cz.month.commission} suffix=" Kč" context={`SK ${fmtEUR(d.sk.month.commission)}`} />
      </ArcSummaryGrid>

      <section className="am-affiliates-section" aria-labelledby="am-affiliates-performance-title">
        <div className="am-affiliates-section-head">
          <div><span>TRHY</span><h2 id="am-affiliates-performance-title">Výkon podle trhu</h2></div>
          <p>Aktuální hodnoty a historický součet</p>
        </div>
        <div className="am-affiliates-markets">
          {perfCard('cz', 'Český trh', d.cz, ' Kč')}
          {perfCard('sk', 'Slovenský trh', d.sk, ' €')}
        </div>
      </section>

      <section className="am-affiliates-section" aria-labelledby="am-affiliates-performance-chart-title">
        <div className="am-affiliates-section-head">
          <div><span>VÝVOJ</span><h2 id="am-affiliates-performance-chart-title">Trend výkonu</h2></div>
          <p>Posledních 12 měsíců</p>
        </div>
        <ArcPanel as="article" className="am-affiliates-chart-card am-affiliates-performance-chart">
          <header>
            <div className="am-affiliates-chart-legend" aria-label="Legenda">
              <span><i className="is-cz" />CZ</span>
              <span><i className="is-sk" />SK</span>
            </div>
            <ArcTabs
              label="Metrika grafu"
              value={metric}
              onChange={setMetric}
              items={[
                { value: 'deposits', label: 'Vklady' },
                { value: 'ngr', label: 'NGR' },
                { value: 'commission', label: 'Provize' },
                { value: 'registrations', label: 'Registrace' },
              ]}
            />
          </header>
          <div className="am-affiliates-chart-canvas am-affiliates-performance-canvas"><Line data={perfChart} options={perfOpts as never} /></div>
        </ArcPanel>
      </section>
    </div>
  );
}

function TabSegmenty() {
  return (
    <div className="tab-content am-affiliates-dashboard">
      <ArcPanel className="am-affiliates-empty">
        <div className="am-affiliates-empty-icon"><i data-feather="layers" /></div>
        <div>
          <span>SEGMENTACE</span>
          <h2>Segmentace zatím nemá data</h2>
          <p>Jakmile budou segmenty zavedené, zobrazí se zde jejich využití a pokrytí affiliate sítě.</p>
        </div>
      </ArcPanel>
    </div>
  );
}

function TabVypis({ vipMode }: { vipMode: VipMode }) {
  const [market, setMarket] = useState<'CZ' | 'SK'>('CZ');
  const [granularity, setGranularity] = useState<'month' | 'year'>('month');
  const [compare, setCompare] = useState<'off' | 'on'>('off');
  const [data, setData] = useState<AffFinanceTimeseries | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailYm, setDetailYm] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getAffilmasterFinanceTimeseries(granularity, vipMode)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [granularity, vipMode]);

  const fm = market === 'CZ' ? fmtCZK : fmtEUR;
  const periodLabel = (p: string) => (granularity === 'year' ? p : ymLabel(p));
  const cmp = compare === 'on';
  const pickMkt = (o: { cz: AffFinancePeriodMetrics; sk: AffFinancePeriodMetrics }) => (market === 'CZ' ? o.cz : o.sk);
  // asc → baseline pro srovnání: aktuální (partial) období proti STEJNÉMU období
  // loňska/min. měsíce (prevSamePeriod), ostatní proti předchozímu řádku. Reverse = novější nahoře.
  const asc = data?.rows ?? [];
  const rows = asc
    .map((r, i) => ({
      period: r.period,
      m: pickMkt(r),
      prev: r.partial && r.prevSamePeriod ? pickMkt(r.prevSamePeriod) : (i > 0 ? pickMkt(asc[i - 1]!) : null),
      partial: !!r.partial,
    }))
    .reverse();
  const tot = rows.reduce((a, r) => ({ reg: a.reg + r.m.registrations, dep: a.dep + r.m.deposits_volume, ngr: a.ngr + r.m.ngr_volume, com: a.com + r.m.commission }), { reg: 0, dep: 0, ngr: 0, com: 0 });
  const tableRows = rows.map((row) => ({
    period: row.period,
    registrations: row.m.registrations,
    deposits: row.m.deposits_volume,
    ngr: row.m.ngr_volume,
    commission: row.m.commission,
    prev: row.prev,
    partial: row.partial,
  }));
  type HistoryRow = (typeof tableRows)[number];
  const numberColumn = (key: 'registrations' | 'deposits' | 'ngr' | 'commission', label: string, previous: keyof AffFinancePeriodMetrics, money: boolean): DataColumn<HistoryRow> => ({
    key,
    label,
    numeric: true,
    sortable: false,
    render: (value, row) => (
      <>
        {money ? fm(Number(value)) : fmt(Number(value))}
        {cmp && <DeltaBadge curr={Number(value)} prev={row.prev?.[previous]} />}
      </>
    ),
  });
  const columns: DataColumn<HistoryRow>[] = [
    {
      key: 'period',
      label: 'Období',
      sortable: false,
      render: (value, row) => granularity === 'month'
        ? <button type="button" className="am-affiliates-period-button" onClick={() => setDetailYm(row.period)}>{periodLabel(String(value))}{row.partial && <span className="am-affiliates-current-dot" aria-label="Aktuální období">●</span>}</button>
        : periodLabel(String(value)),
    },
    numberColumn('registrations', 'Registrace', 'registrations', false),
    numberColumn('deposits', 'Vklady', 'deposits_volume', true),
    numberColumn('ngr', 'NGR', 'ngr_volume', true),
    numberColumn('commission', 'Provize', 'commission', true),
  ];

  return (
    <div className="tab-content am-affiliates-list">
      <ArcToolbar
        className="am-affiliates-component-toolbar"
        label="Filtry výpisu"
        end={<span className="am-affiliates-list-meta">{loading ? 'Načítám…' : `${rows.length} období · ${market === 'CZ' ? 'Kč' : '€'}`}</span>}
      >
        <ArcSelect label="Trh" value={market} onChange={(value) => setMarket(value as 'CZ' | 'SK')} options={[{ value: 'CZ', label: '🇨🇿 CZ' }, { value: 'SK', label: '🇸🇰 SK' }]} />
        <ArcSelect label="Zobrazení" value={granularity} onChange={(value) => setGranularity(value as 'month' | 'year')} options={[{ value: 'month', label: 'Měsíčně' }, { value: 'year', label: 'Ročně' }]} />
        <label className="am-affiliates-compare">
          <input type="checkbox" checked={cmp} onChange={(event) => setCompare(event.target.checked ? 'on' : 'off')} />
          <span>Srovnání</span>
        </label>
      </ArcToolbar>

      {err ? <div className="alert alert-danger">{err}</div> : (
        <>
          {!loading && rows.length > 0 && (
            <ArcSummaryGrid className="am-affiliates-list-summary">
              <MetricCard label="Registrace" value={tot.reg} context="souhrn období" />
              <MetricCard label="Vklady" value={tot.dep} suffix={market === 'CZ' ? ' Kč' : ' €'} context="souhrn období" />
              <MetricCard label="NGR" value={tot.ngr} suffix={market === 'CZ' ? ' Kč' : ' €'} context="souhrn období" />
              <MetricCard label="Provize" value={tot.com} suffix={market === 'CZ' ? ' Kč' : ' €'} context="souhrn období" />
            </ArcSummaryGrid>
          )}
          <SortableDataTable
            rows={tableRows}
            columns={columns}
            rowKey="period"
            caption="Historie výkonu affiliates"
            emptyMessage={loading ? 'Načítám…' : 'Žádná data'}
            itemName={{ one: 'období', other: 'období' }}
          />
        </>
      )}
      <div className="am-affiliates-list-note">
        <span className="am-affiliates-current-dot">●</span> aktuální období (běží) · srovnání proti stejnému období předchozího roku nebo měsíce.
      </div>
      {detailYm && <MonthAffiliatesModal ym={detailYm} market={market} vipMode={vipMode} onClose={() => setDetailYm(null)} />}
    </div>
  );
}

function MonthAffiliatesModal({ ym, market, vipMode, onClose }: { ym: string; market: 'CZ' | 'SK'; vipMode: VipMode; onClose: () => void }) {
  const [data, setData] = useState<AffFinanceMonthDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getAffilmasterFinanceMonthDetail(ym, market, vipMode)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení detailu selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ym, market, vipMode]);
  const fmMoney = market === 'CZ' ? fmtCZK : fmtEUR;
  const tot = (data?.affiliates ?? []).reduce((a, r) => ({
    reg: a.reg + r.registrations, dep: a.dep + r.deposits_volume, ngr: a.ngr + r.ngr_volume, com: a.com + r.commission,
  }), { reg: 0, dep: 0, ngr: 0, com: 0 });
  const partnerRows = (data?.affiliates ?? []).map((a) => ({
    id: a.pap_affiliate_id,
    partner: a.display_name || '–',
    vip: a.vip_partner,
    registrations: a.registrations,
    deposits: a.deposits_volume,
    ngr: a.ngr_volume,
    commission: a.commission,
  }));
  type PartnerRow = (typeof partnerRows)[number];
  const columns: DataColumn<PartnerRow>[] = [
    { key: 'partner', label: 'Affiliate partner', sortable: false, render: (value, row) => <span className="am-affiliates-partner-name">{String(value)}{row.vip && <VipBadge />}</span> },
    { key: 'registrations', label: 'Registrace', numeric: true, sortable: false, render: (value) => fmt(Number(value)) },
    { key: 'deposits', label: 'Vklady', numeric: true, sortable: false, render: (value) => fmMoney(Number(value)) },
    { key: 'ngr', label: 'NGR', numeric: true, sortable: false, render: (value) => fmMoney(Number(value)) },
    { key: 'commission', label: 'Provize', numeric: true, sortable: false, render: (value) => fmMoney(Number(value)) },
  ];
  return (
    <ArcDialog title={'Detail měsíce · ' + ymLabel(ym)} description={(data?.affiliates?.length ?? 0) + ' partnerů s vkladem nebo FTD · ' + market} onClose={onClose} size="wide" className="p1-arc-scope am-affiliates-detail-dialog">
      {err && <div className="alert alert-danger">{err}</div>}
      {!err && loading && <div className="am-affiliates-dialog-loading">Načítám…</div>}
      {!err && !loading && (
        <>
          {partnerRows.length > 0 && <ArcSummaryGrid className="am-affiliates-dialog-summary">
            <MetricCard label="Registrace" value={tot.reg} context="souhrn měsíce" />
            <MetricCard label="Vklady" value={tot.dep} suffix={market === 'CZ' ? ' Kč' : ' €'} context="souhrn měsíce" />
            <MetricCard label="NGR" value={tot.ngr} suffix={market === 'CZ' ? ' Kč' : ' €'} context="souhrn měsíce" />
            <MetricCard label="Provize" value={tot.com} suffix={market === 'CZ' ? ' Kč' : ' €'} context="souhrn měsíce" />
          </ArcSummaryGrid>}
          <SortableDataTable rows={partnerRows} columns={columns} rowKey="id" caption="Affiliate partneři v období" emptyMessage="Žádný partner s provizí v tomto měsíci." />
        </>
      )}
    </ArcDialog>
  );
}

function TabVipSrovnani({ ym }: { ym: string }) {
  const [market, setMarket] = useState<'CZ' | 'SK'>('CZ');
  const [data, setData] = useState<VipComparison | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailYm, setDetailYm] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getAffilmasterVipComparison(ym)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení srovnání selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ym]);

  type ShareMetric = 'ngr_volume' | 'ftd_count' | 'deposits_volume' | 'commission';
  const [shareMetric, setShareMetric] = useState<ShareMetric>('ngr_volume');
  const fm = market === 'CZ' ? fmtCZK : fmtEUR;
  const share = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)} %` : '0 %');
  const monthSplit: VipCompareSplit | null = data ? (market === 'CZ' ? data.month.cz : data.month.sk) : null;
  const totalSplit: VipCompareSplit | null = data ? (market === 'CZ' ? data.total.cz : data.total.sk) : null;
  const rows = data ? [...data.months].reverse().map((m) => ({ ym: m.ym, s: market === 'CZ' ? m.cz : m.sk })) : [];
  const chartRows = data ? [...data.months].sort((a, b) => a.ym.localeCompare(b.ym)).map((m) => ({ ym: m.ym, s: market === 'CZ' ? m.cz : m.sk })) : [];

  const metricMeta: Record<ShareMetric, { label: string; money: boolean }> = {
    ngr_volume: { label: 'NGR', money: true },
    ftd_count: { label: 'FTD', money: false },
    deposits_volume: { label: 'Vklady', money: true },
    commission: { label: 'Provize', money: true },
  };
  const valueFor = (metrics: VipCompareMetrics, key: ShareMetric) => Number(metrics[key]) || 0;
  const dashboardMetrics: MetricsDashboardMetric[] = monthSplit ? (Object.keys(metricMeta) as ShareMetric[]).map((key) => {
    const meta = metricMeta[key];
    const all = valueFor(monthSplit.all, key);
    const only = valueFor(monthSplit.only, key);
    const format = meta.money ? fm : fmt;
    return {
      key,
      label: meta.label,
      value: all,
      delta: null,
      deltaLabel: `${share(only, all)} VIP`,
      deltaDirection: 'neutral',
      points: chartRows.map((row) => ({
        label: ymShort(row.ym),
        value: valueFor(row.s.all, key),
        previous: valueFor(row.s.without, key),
      })),
      format,
      axisFormat: meta.money ? (value: number) => `${fmt(value / 1000)} tis.` : fmt,
    };
  }) : [];
  const breakdownRows = (scope: 'month' | 'total') => {
    if (!data || !monthSplit || !totalSplit) return [];
    const split = scope === 'month' ? monthSplit : totalSplit;
    const partnerValues = data.vipPartners
      .map((partner) => ({
        name: partner.name ?? partner.papAffiliateId,
        value: valueFor(market === 'CZ' ? partner[scope].cz : partner[scope].sk, shareMetric),
        format: metricMeta[shareMetric].money ? fm : fmt,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
    return [
      { name: 'Síť bez VIP', value: valueFor(split.without, shareMetric), format: metricMeta[shareMetric].money ? fm : fmt },
      ...partnerValues,
    ];
  };

  const splitTable = (title: string, note: string, sp: VipCompareSplit) => {
    const metricRows = ([
      { label: 'Registrace', key: 'registrations', money: false },
      { label: 'FTD', key: 'ftd_count', money: false },
      { label: 'Vklady', key: 'deposits_volume', money: true },
      { label: 'NGR', key: 'ngr_volume', money: true },
      { label: 'Provize', key: 'commission', money: true },
    ] as const).map(({ label, key, money }) => ({
      label,
      all: Number(sp.all[key]) || 0,
      without: Number(sp.without[key]) || 0,
      only: Number(sp.only[key]) || 0,
      money,
    }));
    type MetricRow = (typeof metricRows)[number];
    const columns: DataColumn<MetricRow>[] = [
      { key: 'label', label: 'Metrika', sortable: false },
      { key: 'all', label: 'Celkem', numeric: true, sortable: false, render: (value, row) => row.money ? fm(Number(value)) : fmt(Number(value)) },
      { key: 'without', label: 'Bez VIP', numeric: true, sortable: false, render: (value, row) => row.money ? fm(Number(value)) : fmt(Number(value)) },
      { key: 'only', label: 'Jen VIP', numeric: true, sortable: false, render: (value, row) => row.money ? fm(Number(value)) : fmt(Number(value)) },
      { key: 'share', label: 'Podíl VIP', numeric: true, sortable: false, render: (_value, row) => share(row.only, row.all) },
    ];
    return (
      <ArcPanel as="article" className="am-vip-split">
        <div className="am-affiliates-section-head"><div><span>{note.toUpperCase()}</span><h2>{title}</h2></div></div>
        <SortableDataTable rows={metricRows} columns={columns} rowKey="label" caption={title + ' – výkon podle VIP'} />
      </ArcPanel>
    );
  };
  const historyRows = rows.map((row) => ({
    ym: row.ym,
    ngrAll: row.s.all.ngr_volume,
    ngrWithout: row.s.without.ngr_volume,
    ngrOnly: row.s.only.ngr_volume,
    ftdAll: row.s.all.ftd_count,
    ftdOnly: row.s.only.ftd_count,
    commissionAll: row.s.all.commission,
    commissionOnly: row.s.only.commission,
  }));
  type VipHistoryRow = (typeof historyRows)[number];
  const historyMoney = (key: keyof VipHistoryRow, label: string): DataColumn<VipHistoryRow> => ({
    key,
    label,
    numeric: true,
    sortable: false,
    render: (value) => fm(Number(value)),
  });
  const historyColumns: DataColumn<VipHistoryRow>[] = [
    { key: 'ym', label: 'Měsíc', sortable: false, render: (value, row) => <button type="button" className="am-affiliates-period-button" onClick={() => setDetailYm(row.ym)}>{ymLabel(String(value))}</button> },
    historyMoney('ngrAll', 'NGR celkem'),
    historyMoney('ngrWithout', 'NGR bez VIP'),
    historyMoney('ngrOnly', 'NGR jen VIP'),
    { key: 'share', label: 'Podíl VIP', numeric: true, sortable: false, render: (_value, row) => share(row.ngrOnly, row.ngrAll) },
    { key: 'ftdAll', label: 'FTD celkem', numeric: true, sortable: false, render: (value) => fmt(Number(value)) },
    { key: 'ftdOnly', label: 'FTD jen VIP', numeric: true, sortable: false, render: (value) => fmt(Number(value)) },
    historyMoney('commissionAll', 'Provize celkem'),
    historyMoney('commissionOnly', 'Provize jen VIP'),
  ];

  return (
    <div className="tab-content am-affiliates-list am-vip-compare">
      {err && <div className="alert alert-danger">{err}</div>}
      {!err && loading && !data && <div className="prototype-page-loading" aria-label="Načítám"><span /><span /><span /><span /><span /></div>}
      {data && monthSplit && totalSplit && (
        <>
          <div className="am-vip-arc p1-arc-scope">
            <MetricsDashboard
              title="Affilmaster síť a VIP partneři"
              subtitle={`${ymLabel(data.ym)} · ${market === 'CZ' ? 'český trh' : 'slovenský trh'}`}
              metrics={dashboardMetrics}
              metricValue={shareMetric}
              onMetricChange={(value) => setShareMetric(value as ShareMetric)}
              segments={[{ value: 'CZ', label: 'CZ', ariaLabel: 'Český trh' }, { value: 'SK', label: 'SK', ariaLabel: 'Slovenský trh' }]}
              segmentValue={market}
              onSegmentChange={(value) => setMarket(value === 'SK' ? 'SK' : 'CZ')}
              segmentLabel="Trh"
              compareLabel="Bez VIP"
              compareSeriesLabel="Bez VIP"
              breakdowns={[
                { title: ymLabel(data.ym), column: metricMeta[shareMetric].label, rows: breakdownRows('month') },
                { title: 'Celá historie', column: metricMeta[shareMetric].label, rows: breakdownRows('total') },
              ]}
              ariaLabel="Srovnání výkonu Affilmaster sítě a VIP partnerů"
            />
          </div>

          <section className="am-affiliates-section am-vip-summary-section">
            <div className="am-affiliates-section-head">
              <div><span>SOUHRN</span><h2>Výkon podle započtení VIP</h2></div>
              <p>Celá síť, síť bez VIP účtů a samostatný výkon VIP partnerů.</p>
            </div>
            <div className="am-vip-split-grid">
              {splitTable(ymLabel(data.ym), 'vybraný měsíc', monthSplit)}
              {splitTable('Celá historie', 'celkem', totalSplit)}
            </div>
          </section>

          <section className="am-affiliates-section am-vip-history-section">
            <div className="am-affiliates-section-head"><div><span>VÝVOJ</span><h2>Posledních 24 měsíců</h2></div><p>NGR a FTD celkem, bez VIP a jen VIP. Kliknutím na měsíc otevřeš rozpad po VIP partnerech a kasinech.</p></div>
            <SortableDataTable rows={historyRows} columns={historyColumns} rowKey="ym" caption="Vývoj výkonu VIP partnerů" />
          </section>

          {detailYm && <VipMonthModal ym={detailYm} onClose={() => setDetailYm(null)} />}
        </>
      )}
    </div>
  );
}

function VipMonthModal({ ym, onClose }: { ym: string; onClose: () => void }) {
  const [data, setData] = useState<VipMonthBreakdown | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    getAffilmasterVipMonthBreakdown(ym)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Načtení rozpadu selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ym]);
  const money = (v: number, cur: 'CZK' | 'EUR') => (cur === 'CZK' ? fmtCZK(v) : fmtEUR(v));
  const partners = data?.partners ?? [];
  const instCount = partners.reduce((n, p) => n + p.instances.length, 0);
  const instanceRows = partners.flatMap((partner) => partner.instances.map((instance) => ({
    id: partner.papAffiliateId + '|' + instance.code,
    partner: partner.name ?? partner.papAffiliateId,
    casino: instance.casino,
    currency: instance.currency,
    registrations: instance.month.registrations,
    ftd: instance.month.ftd_count,
    deposits: instance.month.deposits_volume,
    ngr: instance.month.ngr_volume,
    commission: instance.month.commission,
  })));
  type InstanceRow = (typeof instanceRows)[number];
  const instanceMoney = (key: keyof InstanceRow, label: string): DataColumn<InstanceRow> => ({
    key,
    label,
    numeric: true,
    sortable: false,
    render: (value, row) => money(Number(value), row.currency),
  });
  const columns: DataColumn<InstanceRow>[] = [
    { key: 'partner', label: 'Partner / kasino', sortable: false, render: (value, row) => <span className="am-affiliates-vip-identity"><strong>{String(value)}<VipBadge /></strong><span>{row.casino} · {row.currency === 'CZK' ? 'Kč' : '€'}</span></span> },
    { key: 'registrations', label: 'Registrace', numeric: true, sortable: false, render: (value) => fmt(Number(value)) },
    { key: 'ftd', label: 'FTD', numeric: true, sortable: false, render: (value) => fmt(Number(value)) },
    instanceMoney('deposits', 'Vklady'),
    instanceMoney('ngr', 'NGR'),
    instanceMoney('commission', 'Provize'),
  ];
  return (
    <ArcDialog title={'VIP rozpad · ' + ymLabel(ym)} description={partners.length + ' partneři · ' + instCount + ' účtů v instancích kasin · částky v měně instance'} onClose={onClose} size="wide" className="p1-arc-scope am-affiliates-detail-dialog">
      {err && <div className="alert alert-danger">{err}</div>}
      {!err && loading && <div className="am-affiliates-dialog-loading">Načítám…</div>}
      {!err && !loading && <SortableDataTable rows={instanceRows} columns={columns} rowKey="id" caption="VIP partneři v měsíci" emptyMessage="Žádná VIP data v tomto měsíci." />}
      <p className="am-vip-foot">Provize je odměna, kterou VIP partnerovi vyplácí kasino. Do vyúčtování Affilmasteru vstupuje jen provize z NGR u klientů s volbou „Účtovat i VIP“.</p>
    </ArcDialog>
  );
}
